import { isOpenIncidentState, isOperationallyLiveIncidentState } from "../../domain/status-presentation";

/**
 * Which incident an *implicit* default should land on.
 *
 * The discovery projection is ordered newest first, and the store used to take
 * the first non-terminal entry. `REPORTED` is non-terminal, so a report taken
 * five minutes ago outranked the operation that had been running for hours:
 * opening `/operations` bound the whole workspace to `INC-2026-000027`
 * (`REPORTED`) while `INC-2026-000026` (`ACTIVE`) held the missions, packages
 * and orders. Every panel below then correctly showed nothing — the counts
 * were right about the wrong incident, which is the worst kind of empty screen
 * because it looks like missing data rather than a wrong binding.
 *
 * So rank rather than filter. A pre-operational incident is still a legitimate
 * default when it is all there is — an OV with one fresh report and nothing
 * running must still land somewhere — it just never displaces an incident that
 * is actually being worked.
 *
 * This governs implicit defaulting ONLY. Explicit intent (a deep link, a
 * restored selection, an operator picking from the rail) resolves whatever it
 * names, including a terminal incident, so review and reopening keep working.
 */
export function preferOperationalIncident<T extends { readonly state: string }>(
  candidates: readonly T[],
): T | undefined {
  return (
    candidates.find((item) => isOperationallyLiveIncidentState(item.state)) ??
    candidates.find((item) => isOpenIncidentState(item.state))
  );
}
